import React, { useState, useEffect, useRef } from 'react';
import { Download, Maximize2, Minimize2 } from 'lucide-react';
import { getDocumentos, subirDocumentos, eliminarDocumento, urlVerDoc, urlDescargar, getPlazo, setOverride, getServerUrl, descargarEnNavegador } from '../utils/api';
import PdfViewer from '../components/PdfViewer';

function formatBytes(b) {
  if (!b) return '—';
  if (b < 1024) return b + ' B';
  if (b < 1048576) return (b / 1024).toFixed(1) + ' KB';
  return (b / 1048576).toFixed(1) + ' MB';
}
function formatDate(s) {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('es-BO', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
function formatCountdown(ms) {
  if (ms <= 0) return null;
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  if (h > 48) { const d = Math.floor(h / 24); return `${d}d ${h % 24}h restantes`; }
  if (h > 0) return `${h}h ${String(m).padStart(2,'0')}m para el plazo`;
  return `${m}m ${String(s).padStart(2,'0')}s para el plazo`;
}

const ESTADO_CONFIG = {
  sin_fecha:  { color: '#888',    bg: '#f5f5f5', label: 'Sin convocatoria',   icon: '○' },
  abierto:    { color: '#166534', bg: '#dcfce7', label: 'Plazo abierto',       icon: '✓' },
  cerrado:    { color: '#854d0e', bg: '#fef3c7', label: 'Plazo cerrado (48h)', icon: '⏰' },
  realizada:  { color: '#1d4ed8', bg: '#eff6ff', label: 'Sesión realizada',    icon: '✓' },
};

export default function SesionView({ sesion, user, onRefresh }) {
  const [docs, setDocs] = useState([]);
  const [active, setActive] = useState(null);
  const [pdfSrc, setPdfSrc] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(0);
  const [pdfError, setPdfError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [docCacheStatus, setDocCacheStatus] = useState({});
  const [plazo, setPlazo] = useState(null);
  const [pantallaCompleta, setPantallaCompleta] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [togglingOverride, setTogglingOverride] = useState(false);
  const fileRef = useRef();
  const countdownRef = useRef();

  useEffect(() => {
    if (!sesion) return;
    setActive(null); setPdfSrc(null); setPantallaCompleta(false);
    setError('');
    setPdfError(null);
    setDocs([]);
    loadDocs();
    loadPlazo();
    const interval = setInterval(() => loadDocs(true), 20000);
    const plazoInterval = setInterval(loadPlazo, 60000);
    return () => { clearInterval(interval); clearInterval(plazoInterval); };
  }, [sesion?.id]);

  // Cuenta regresiva en tiempo real
  useEffect(() => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    if (!plazo?.ms_restantes || plazo.estado !== 'abierto') { setCountdown(null); return; }
    let ms = plazo.ms_restantes - (Date.now() - (plazo._fetchedAt || Date.now()));
    const tick = () => {
      ms -= 1000;
      setCountdown(ms > 0 ? formatCountdown(ms) : null);
      if (ms <= 0) { clearInterval(countdownRef.current); loadPlazo(); }
    };
    countdownRef.current = setInterval(tick, 1000);
    setCountdown(formatCountdown(ms));
    return () => clearInterval(countdownRef.current);
  }, [plazo]);

  useEffect(() => {
    if (!window.electronAPI) return;
    window.electronAPI.onDownloadProgress(({ docId, pct }) => {
      setPdfProgress(pct);
      setDocCacheStatus(s => ({ ...s, [docId]: 'downloading' }));
    });
    return () => window.electronAPI.offDownloadProgress();
  }, []);

  const docsKey = `cache_docs_${sesion?.id}`;

  const saveDocsCache = (data) => {
    try { localStorage.setItem(docsKey, JSON.stringify(data)); } catch {}
  };
  const loadDocsCache = () => {
    try {
      const raw = localStorage.getItem(docsKey);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  };

  const loadPlazo = async () => {
    if (!sesion) return;
    try {
      const data = await getPlazo(sesion.id);
      data._fetchedAt = Date.now();
      setPlazo(data);
    } catch {}
  };

  const loadDocs = async (silent = false) => {
    try {
      const data = await getDocumentos(sesion.id);
      setDocs(data);
      saveDocsCache(data); // guardar para uso offline
      if (window.electronAPI) {
        const statuses = {};
        for (const doc of data) {
          const c = await window.electronAPI.pdfCacheCheck(doc.id);
          statuses[doc.id] = c ? 'cached' : null;
        }
        setDocCacheStatus(statuses);
      }
    } catch {
      // Sin conexion — cargar desde cache local
      if (!silent) {
        try {
          const key = 'cache_docs_' + sesion?.id;
          const raw = localStorage.getItem(key);
          const cached = raw ? JSON.parse(raw) : null;
          if (cached !== null) {
            setDocs(cached);
            if (window.electronAPI) {
              const statuses = {};
              for (const doc of cached) {
                const c = await window.electronAPI.pdfCacheCheck(doc.id);
                statuses[doc.id] = c ? 'cached' : null;
              }
              setDocCacheStatus(statuses);
            }
          } else {
            setError('Sin conexion — no hay datos guardados para esta sesion');
          }
        } catch {
          setError('Sin conexion — no hay datos guardados para esta sesion');
        }
      }
    }
  };

  const handleToggleOverride = async () => {
    if (!plazo) return;
    const nuevoEstado = !plazo.plazo_override;
    if (nuevoEstado && !window.confirm(
      '¿Habilitar excepción de plazo?\n\nEsto permitirá subir y eliminar documentos aunque el plazo esté cerrado.\n\nEsta acción queda registrada.'
    )) return;
    setTogglingOverride(true);
    try {
      await setOverride(sesion.id, nuevoEstado);
      await loadPlazo();
    } catch { setError('Error al cambiar el estado del override'); }
    setTogglingOverride(false);
  };

  const canEdit = () => {
    if (!plazo) return true; // sin info de plazo, no bloquear
    if (plazo.plazo_override) return true; // override activo
    return plazo.estado === 'sin_fecha' || plazo.estado === 'abierto';
  };

  const openPdf = async (doc) => {
    setPdfError(null);
    if (active?.id === doc.id && pdfSrc) return;
    setActive(doc); setPdfSrc(null); setPdfProgress(0); setPdfLoading(true);
    if (window.electronAPI) {
      const cached = await window.electronAPI.pdfCacheCheck(doc.id);
      if (cached) { setPdfSrc(cached); setPdfLoading(false); return; }
      try {
        const token = localStorage.getItem('token');
        const url = `${getServerUrl()}/api/documentos/${doc.id}/ver?token=${token}`;
        const pdfUrl = await window.electronAPI.pdfCacheDownload({ docId: doc.id, url });
        setPdfSrc(pdfUrl);
        setDocCacheStatus(s => ({ ...s, [doc.id]: 'cached' }));
      } catch (err) {
        // No crear nada en caché — mostrar mensaje en el visor
        const esSinConexion = err === 'SIN_CONEXION' || String(err).includes('SIN_CONEXION');
        setPdfError(esSinConexion
          ? 'Sin conexión a internet. Conéctese a la red para ver este documento.'
          : 'No se pudo descargar el documento. Intente nuevamente.');
      } finally {
        setPdfLoading(false);
        setPdfProgress(0);
      }
    } else {
      setPdfSrc(urlVerDoc(doc.id)); setPdfLoading(false);
    }
  };

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;
    if (!canEdit()) { setError('El plazo está cerrado. Active la excepción para subir documentos.'); return; }
    setUploading(true); setUploadProgress(0); setError('');
    try {
      await subirDocumentos(sesion.id, Array.from(files), setUploadProgress);
      await loadDocs(); onRefresh();
    } catch (e) {
      const msg = e.response?.data?.error || 'Error al subir archivos';
      setError(msg);
    } finally { setUploading(false); setUploadProgress(0); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFileSelect(Array.from(e.dataTransfer.files).filter(f => f.type === 'application/pdf'));
  };

  const handleEliminar = async (doc) => {
    if (!canEdit()) { setError('El plazo está cerrado. Active la excepción para eliminar documentos.'); return; }
    if (!window.confirm(`¿Eliminar "${doc.nombre_original}"?`)) return;
    try {
      await eliminarDocumento(doc.id);
      if (window.electronAPI) await window.electronAPI.pdfCacheDelete(doc.id);
      if (active?.id === doc.id) { setActive(null); setPdfSrc(null); setPantallaCompleta(false); }
      await loadDocs(); onRefresh();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al eliminar');
    }
  };

  const handleDescargar = async (doc) => {
    // Si ya está en la caché local, se guarda directo desde ahí (sin red)
    if (window.electronAPI && docCacheStatus[doc.id] === 'cached') {
      const result = await window.electronAPI.pdfCacheSaveAs({ docId: doc.id, nombreSugerido: doc.nombre_original });
      if (result.ok || result.reason === 'CANCELADO') return;
      // Si por algún motivo falló guardar desde caché, seguir con la descarga normal
    }
    const url = urlDescargar(doc.id);
    if (window.electronAPI) window.electronAPI.openExternal(url);
    else {
      try { await descargarEnNavegador(url, doc.nombre_original); }
      catch { window.open(url, '_blank'); }
    }
  };

  if (!sesion) return (
    <div className="empty-state">
      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
      </svg>
      <h3>Seleccione una sesión</h3>
      <p>Elija una sesión del panel izquierdo para ver sus documentos</p>
    </div>
  );

  const estadoConf = plazo ? (ESTADO_CONFIG[plazo.estado] || ESTADO_CONFIG.sin_fecha) : null;
  const bloqueado = plazo && !canEdit();

  return (
    <>
      <div className="topbar">
        <div style={{ flex: 1 }}>
          <div className="topbar-title">
            {sesion.tipo === 'ordinaria' ? 'Sesión Ordinaria' : 'Sesión Extraordinaria'} N°{String(sesion.numero).padStart(2,'0')} — Gestión {sesion.gestion?.anio_inicio}-{sesion.gestion?.anio_fin}
          </div>
          <div className="topbar-sub" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span>{sesion.fecha_hora ? `Sesión: ${new Date(sesion.fecha_hora).toLocaleString('es-BO', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit', timeZone:'America/La_Paz' })}` : 'Sin fecha de convocatoria'}</span>
            <span>·</span>
            <span>{docs.length} documento{docs.length !== 1 ? 's' : ''}</span>
            {countdown && <span style={{ color: '#854d0e', fontWeight: 500 }}>· ⏰ {countdown}</span>}
          </div>
        </div>
        <span className={`badge ${sesion.tipo === 'ordinaria' ? 'badge-ord' : 'badge-ext'}`}>
          {sesion.tipo === 'ordinaria' ? 'Ordinaria' : 'Extraordinaria'}
        </span>
        {/* Badge de estado de plazo — solo relevante para admin (es quien sube/elimina) */}
        {estadoConf && user.rol === 'admin' && (
          <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 500, background: estadoConf.bg, color: estadoConf.color }}>
            {estadoConf.icon} {estadoConf.label}
          </span>
        )}
        {/* Botón override — solo admin */}
        {user.rol === 'admin' && plazo && (plazo.estado === 'cerrado' || plazo.estado === 'realizada') && (
          <button
            className={`btn-action btn-sm ${plazo.plazo_override ? 'btn-outline' : 'btn-red'}`}
            onClick={handleToggleOverride}
            disabled={togglingOverride}
            title={plazo.plazo_override ? 'Deshabilitar excepción de plazo' : 'Habilitar excepción de plazo (plazo cerrado)'}
          >
            {plazo.plazo_override ? '🔓 Excepción activa' : '🔒 Habilitar excepción'}
          </button>
        )}
      </div>

      {/* Banner de plazo cerrado — solo visible para admin */}
      {bloqueado && user.rol === 'admin' && (
        <div style={{ padding: '8px 20px', background: plazo.estado === 'realizada' ? '#eff6ff' : '#fefce8', borderBottom: `2px solid ${plazo.estado === 'realizada' ? '#93c5fd' : '#fde047'}`, display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
          <span style={{ fontSize: 16 }}>{plazo.estado === 'realizada' ? 'ℹ️' : '⏰'}</span>
          <span style={{ color: plazo.estado === 'realizada' ? '#1d4ed8' : '#854d0e', fontWeight: 500 }}>
            {plazo.estado === 'realizada'
              ? 'Esta sesión ya fue realizada. Subida y eliminación bloqueadas.'
              : 'Plazo cerrado — faltan menos de 48 horas para la sesión.'}
          </span>
          <span style={{ color: '#666', marginLeft: 'auto', fontSize: 12 }}>
            Puede habilitar una excepción desde el botón superior.
          </span>
        </div>
      )}

      {/* Banner de override activo — solo visible para admin */}
      {plazo?.plazo_override && user.rol === 'admin' && (
        <div style={{ padding: '6px 20px', background: '#fff7ed', borderBottom: '1px solid #fed7aa', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#9a3412' }}>
          <span>⚠️</span>
          <span>Excepción de plazo activa — se permite subir y eliminar documentos fuera del plazo reglamentario.</span>
        </div>
      )}

      <div className={`content-split ${active ? 'has-active' : ''}`}>
        <div className="file-panel">
          <div className="file-panel-header">
            <span className="file-panel-title">Documentos ({docs.length})</span>
          </div>
          <div className="file-list">
            {error && <div className="alert alert-err" style={{ fontSize: 12, marginBottom: 8 }}>{error}<button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button></div>}
            {docs.length === 0 && !uploading && (
              <div style={{ textAlign: 'center', padding: '32px 16px', color: '#aaa', fontSize: 12 }}>No hay documentos en esta sesión</div>
            )}
            {docs.map(doc => {
              const status = docCacheStatus[doc.id];
              return (
                <div key={doc.id} className={`file-card ${active?.id === doc.id ? 'active' : ''}`} onClick={() => openPdf(doc)}>
                  <div className="file-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
                    </svg>
                  </div>
                  <div className="file-info">
                    <div className="file-name" title={doc.nombre_original}>{doc.nombre_original}</div>
                    <div className="file-meta" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>{formatBytes(doc.tamano)} · {formatDate(doc.subido_en)}</span>
                      {status === 'cached' && <span title="Guardado localmente" style={{ fontSize: 10, background: '#dcfce7', color: '#166534', borderRadius: 4, padding: '1px 5px' }}>⚡ local</span>}
                      {status === 'downloading' && <span style={{ fontSize: 10, background: '#fef9c3', color: '#854d0e', borderRadius: 4, padding: '1px 5px' }}>↓ descargando...</span>}
                    </div>
                    <div className="file-actions">
                      <button className="file-btn" onClick={e => { e.stopPropagation(); openPdf(doc); }}>Ver</button>
                      <button className="file-btn" onClick={e => { e.stopPropagation(); handleDescargar(doc); }}>Descargar</button>
                      {user.rol === 'admin' && (
                        <button
                          className="file-btn red"
                          onClick={e => { e.stopPropagation(); handleEliminar(doc); }}
                          title={bloqueado ? 'Plazo cerrado — habilite la excepción primero' : ''}
                          style={{ opacity: bloqueado ? 0.4 : 1 }}
                        >
                          Eliminar
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {user.rol === 'admin' && (
            <div className="upload-area">
              <div
                className="upload-drop"
                style={{ opacity: bloqueado ? 0.4 : 1, cursor: bloqueado ? 'not-allowed' : 'pointer', borderColor: bloqueado ? '#ddd' : undefined }}
                onDragOver={e => { if (!bloqueado) e.preventDefault(); }}
                onDrop={e => { if (!bloqueado) handleDrop(e); }}
                onClick={() => { if (!bloqueado) fileRef.current?.click(); }}
                title={bloqueado ? 'Plazo cerrado — habilite la excepción primero' : ''}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={bloqueado ? '#aaa' : '#c8102e'} strokeWidth="1.5">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <p style={{ color: bloqueado ? '#aaa' : undefined }}>
                  {bloqueado ? 'Subida bloqueada — plazo cerrado' : uploading ? `Subiendo... ${uploadProgress}%` : 'Arrastra PDFs aquí o haz clic'}
                </p>
              </div>
              {uploading && (
                <div className="upload-progress">
                  <div className="upload-progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                </div>
              )}
              <input ref={fileRef} type="file" multiple accept=".pdf" style={{ display: 'none' }} onChange={e => handleFileSelect(e.target.files)} />
            </div>
          )}
        </div>

        <div className={`viewer-panel ${pantallaCompleta ? 'pantalla-completa' : ''}`}>
          {active ? (
            <>
              <div className="viewer-toolbar">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>{active.nombre_original}</span>
                  {pdfLoading && pdfProgress > 0 && <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>Descargando {pdfProgress}%...</span>}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="viewer-btn" title="Pantalla completa" onClick={() => setPantallaCompleta(v => !v)}>
                    {pantallaCompleta ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
                  </button>
                  <button className="viewer-btn" title="Descargar" onClick={() => handleDescargar(active)}><Download size={15} /></button>
                  <button className="viewer-btn" onClick={() => { setActive(null); setPdfSrc(null); setPantallaCompleta(false); }}>✕</button>
                </div>
              </div>
              {pdfLoading && pdfProgress > 0 && (
                <div style={{ width: '100%', height: 3, background: 'rgba(255,255,255,0.1)', flexShrink: 0 }}>
                  <div style={{ height: '100%', background: '#c8102e', width: `${pdfProgress}%`, transition: 'width 0.2s' }}></div>
                </div>
              )}
              {pdfLoading && (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, color: 'rgba(255,255,255,0.4)' }}>
                  <p style={{ fontSize: 13 }}>{pdfProgress > 0 ? `Descargando... ${pdfProgress}%` : 'Preparando documento...'}</p>
                </div>
              )}
              {!pdfLoading && pdfError && (
                <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:16, color:'rgba(255,255,255,0.5)', textAlign:'center', padding:40 }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" style={{opacity:0.3}}>
                    <path d="M1 1l22 22M16.72 11.06A10.94 10.94 0 0 1 19 12.55M5 12.55a10.94 10.94 0 0 1 5.17-2.39M10.71 5.05A16 16 0 0 1 22.56 9M1.42 9a15.91 15.91 0 0 1 4.7-2.88M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01"/>
                  </svg>
                  <div>
                    <p style={{fontSize:14, marginBottom:6}}>{pdfError}</p>
                    <button
                      className="viewer-btn"
                      onClick={() => { setPdfError(null); openPdf(active); }}
                      style={{marginTop:8}}
                    >
                      Reintentar
                    </button>
                  </div>
                </div>
              )}

              {!pdfLoading && !pdfError && pdfSrc && (
                window.electronAPI ? (
                  <iframe
                    key={pdfSrc}
                    src={pdfSrc}
                    style={{ flex: 1, width: '100%', border: 'none', display: 'block' }}
                    title={active.nombre_original}
                  />
                ) : (
                  <PdfViewer key={pdfSrc} src={pdfSrc} />
                )
              )}
            </>
          ) : (
            <div className="viewer-empty">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
              <p>Selecciona un documento para visualizarlo</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
